import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Paper, Typography, Chip, Grid, TextField, Button,
  Select, MenuItem, InputLabel, FormControl, Dialog,
  DialogTitle, DialogContent, DialogActions, Tabs, Tab,
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import MonitorIcon from '@mui/icons-material/Monitor';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PaymentsIcon from '@mui/icons-material/Payments';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import DescriptionIcon from '@mui/icons-material/Description';
import Layout from '../../components/layout/Layout';
import MenuPriceEditor from './MenuPriceEditor';
import PriceRequestForm from './PriceRequestForm';
import { getStores } from '../../Redux/Actions/storeAction';
import { getScreens } from '../../Redux/Actions/screenAction';
import { addChangeRequest, getChangeRequests } from '../../Redux/Actions/changeRequestAction';
import { getPayments } from '../../Redux/Actions/paymentAction';
import { api } from '../../api/api';
import './storeportal.css';
import OnboardingProgress from './OnboardingProgress';

const REQUEST_TYPES = {
  price_change: '가격 변경',
  size_change: '사이즈 변경',
  menu_update: '메뉴 업데이트',
  other: '기타',
};

const STATUS_COLORS = {
  received: { bg: '#fff3e0', color: '#e65100', label: '접수' },
  in_progress: { bg: '#e3f2fd', color: '#1565c0', label: '진행 중' },
  customer_confirm: { bg: '#fef9c3', color: '#a16207', label: '고객 확인 대기' },
  confirmed: { bg: '#dbeafe', color: '#1d4ed8', label: '확인 완료' },
  updating: { bg: '#ede9fe', color: '#7c3aed', label: '업데이트 중' },
  completed: { bg: '#e8f5e9', color: '#2e7d32', label: '완료' },
  rejected: { bg: '#fce4ec', color: '#c62828', label: '반려' },
  pending: { bg: '#fff3e0', color: '#e65100', label: '대기중' },
};

const emptyRequestForm = {
  type: 'price_change',
  menuCode: '',
  menuName: '',
  changeDetails: '',
};

function storePortalUserId(user) {
  if (!user) return null;
  if (user.role === 'admin') return user._id;
  return user.user?._id || user._id;
}

function storePortalOpts(user) {
  if (!user) return {};
  if (user.role === 'admin') return {};
  return {
    role: user.role,
    subuserId: user._id,
    tljRegion: user.tljRegion,
  };
}

function StorePortal({ user }) {
  const dispatch = useDispatch();
  const { stores } = useSelector(s => s.Store);
  const { screens } = useSelector(s => s.Screen);
  const { requests } = useSelector(s => s.ChangeRequest);
  const { payments } = useSelector(s => s.Payment);

  const [requestForm, setRequestForm] = useState(emptyRequestForm);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [requestStoreId, setRequestStoreId] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const userId = storePortalUserId(user);
  const opts = storePortalOpts(user);

  useEffect(() => {
    if (userId) {
      dispatch(getStores(userId, opts));
      dispatch(getScreens(user?._id));
      dispatch(getChangeRequests(userId, { subuserId: opts.subuserId }));
    }
  }, [user?._id, userId, opts.role, opts.subuserId, opts.tljRegion]);

  useEffect(() => {
    if (userId && stores?.length > 0) {
      const ids = stores.map(s => s._id);
      dispatch(getPayments(userId, { storeIds: ids, mine: user?.role !== 'admin' }));
    }
  }, [userId, stores?.length]);



  const myStores = stores || [];
  const myStoreIds = myStores.map(s => s._id);
  const selectedStores = myStores.filter(s => selectedStoreIds.includes(s._id));
  const singleStore = myStores.length === 1 ? myStores[0] : null;
  const myStore = singleStore || (selectedStoreIds[0] ? myStores.find(s => s._id === selectedStoreIds[0]) : myStores[0]);

  const screensForStore = (storeId) =>
    (screens || []).filter(s => String(s.store?._id || s.store) === String(storeId));
  const allScreens = myStoreIds.flatMap(sid => screensForStore(sid));

  const handleFormChange = (e) => {
    setRequestForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmitRequest = async () => {
    if (!requestForm.changeDetails.trim()) return;
    const storeId = requestStoreId || myStore?._id;
    if (!storeId) return;
    setSubmitting(true);
    await dispatch(addChangeRequest({
      ...requestForm,
      user: userId,
      store: storeId,
      requestedBy: user?.role !== 'admin' ? user?._id : undefined,
    }));
    setRequestForm(emptyRequestForm);
    setDialogOpen(false);
    setRequestStoreId('');
    setSubmitting(false);
    dispatch(getChangeRequests(userId, { subuserId: opts.subuserId }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return typeof dateStr === 'string' ? dateStr.split('T')[0] : new Date(dateStr).toISOString().split('T')[0];
  };

  const titleByRole = {
    store_owner: 'My Store (점포)',
    store_manager: '담당 매장',
    tlj_manager: '담당 스토어 현황',
    tlj_admin: 'TLJ 현황',
  };
  const portalTitle = titleByRole[user?.role] || 'My Store Portal';

  return (
    <Layout title="My Store" user={user}>
      <Box sx={{ p: 2, maxWidth: 960, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <StorefrontIcon sx={{ color: '#551FFF', fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#551FFF' }}>
            {portalTitle}
          </Typography>
        </Box>

        {myStores.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography color="text.secondary">
              배정된 점포가 없습니다. 관리자에게 문의하세요.
            </Typography>
          </Paper>
        ) : (
          <>
            {/* 오픈 준비 중인 매장마다 진행 카드 (12.01-D011) */}
            {myStores.filter(s => s.lifecycle === 'onboarding').map(s => <OnboardingProgress key={s._id} storeId={s._id} storeName={myStores.length > 1 ? s.name : null} />)}
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 2, borderColor: 'divider', mb: 2 }}
              variant="scrollable" scrollButtons="auto">
              <Tab label="스토어 정보" />
              <Tab label="결제 정보" />
              <Tab label="변경 요청" />
              <Tab label="스크린" />
              <Tab label="메뉴 가격" icon={<RestaurantMenuIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
              <Tab label="계약서" icon={<DescriptionIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
            </Tabs>

            {/* 탭 1: 스토어 정보 — 컴팩트 카드 */}
            {activeTab === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {myStores.map(store => {
                  const addr = [store.address1, store.address2, store.town, store.state, store.zipcode].filter(Boolean).join(', ');
                  return (
                    <Paper key={store._id} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                      {/* Header bar */}
                      <Box sx={{ background: 'linear-gradient(135deg, #551FFF, #7c3aed)', color: '#fff', p: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{store.name || store.tljNo}</Typography>
                          {store.tljNo && <Typography sx={{ fontSize: 12, opacity: 0.8 }}>{store.tljNo} {store.tljRegion && `· ${store.tljRegion}`}</Typography>}
                        </Box>
                        <Chip
                          label={store.status === 'active' ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{ background: store.status === 'active' ? 'rgba(255,255,255,0.2)' : 'rgba(239,68,68,0.3)', color: '#fff', fontWeight: 600, fontSize: 11 }}
                        />
                      </Box>
                      {/* Info grid */}
                      <Box sx={{ p: '16px 20px' }}>
                        <Grid container spacing={1.5}>
                          {[
                            { icon: '📍', label: 'Address', value: addr || '-' },
                            { icon: '📞', label: 'Phone', value: store.storePhone || '-' },
                            { icon: '📅', label: 'Open', value: formatDate(store.openDate) },
                            { icon: '💳', label: 'Plan', value: store.paymentOption === 'yearly' ? 'Yearly' : 'Monthly' },
                          ].map(({ icon, label, value }) => (
                            <Grid item xs={6} key={label}>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <span style={{ fontSize: 16 }}>{icon}</span>
                                <Box>
                                  <Typography sx={{ fontSize: 10, color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
                                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#333' }}>{value}</Typography>
                                </Box>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            )}

            {/* 탭 2: 결제 정보 */}
            {activeTab === 1 && (
              <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(85,31,255,0.08)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PaymentsIcon sx={{ color: '#551FFF', fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#551FFF' }}>
                    결제 정보
                  </Typography>
                </Box>
                {(!payments || payments.length === 0) ? (
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center', fontSize: 14 }}>
                    결제 이력이 없습니다.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {payments.map(p => (
                      <Box key={p._id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, border: '1px solid #eee', borderRadius: 1 }}>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {p.store?.tljNo || p.store?.name || '점포'} · {p.invoiceNumber || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(p.dueDate)} · {p.status} · ${Number(p.amount).toFixed(2)}
                          </Typography>
                        </Box>
                        <Chip
                          label={p.status}
                          size="small"
                          sx={{
                            background: p.status === 'paid' ? '#e8f5e9' : p.status === 'overdue' ? '#fce4ec' : '#fff3e0',
                            color: p.status === 'paid' ? '#2e7d32' : p.status === 'overdue' ? '#c62828' : '#e65100',
                            fontWeight: 600, fontSize: 11,
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            )}

            {/* 탭 3: 변경 요청 / 히스토리 */}
            {activeTab === 2 && (
              <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(85,31,255,0.08)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#551FFF' }}>
                    변경 요청 / 히스토리
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => { setRequestStoreId(myStore?._id || ''); setDialogOpen(true); }}
                    sx={{ background: '#551FFF', borderRadius: 2, fontSize: 13 }}
                  >
                    기타 요청
                  </Button>
                </Box>

                {/* 가격 변경 인라인 폼 */}
                <PriceRequestForm
                  store={myStore}
                  userId={userId}
                  user={user}
                  subuserId={opts.subuserId}
                  onSubmitted={() => dispatch(getChangeRequests(userId, { subuserId: opts.subuserId }))}
                />
                {(!requests || requests.length === 0) ? (
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center', fontSize: 14 }}>
                    제출된 요청이 없습니다.
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {requests.map(req => (
                      <RequestCard key={req._id} req={req} />
                    ))}
                  </Box>
                )}
              </Paper>
            )}

            {/* 탭 4: 현재 화면 (스크린) */}
            {activeTab === 3 && (
              <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(85,31,255,0.08)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <MonitorIcon sx={{ color: '#551FFF', fontSize: 20 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#551FFF' }}>
                    스크린 현황
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({allScreens.length}개)
                  </Typography>
                </Box>
                {allScreens.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center', fontSize: 14 }}>
                    연결된 스크린이 없습니다.
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {myStores.map(store => (
                      <Grid item xs={12} key={store._id}>
                        <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 1 }}>
                          {store.tljNo || store.name}
                        </Typography>
                        <Grid container spacing={2}>
                          {screensForStore(store._id).map(screen => (
                            <Grid item xs={12} sm={6} md={4} key={screen._id}>
                              <Box className={`sp-screen-card ${screen.status ? 'sp-screen-card--online' : 'sp-screen-card--offline'}`}>
                                <div className="sp-screen-code">CODE: {screen.code}</div>
                                <div className="sp-screen-name">{screen.name || '-'}</div>
                                <div className="sp-screen-playlist">
                                  {screen.playlist?.name || '플레이리스트 없음'}
                                </div>
                                <Chip
                                  label={screen.status ? 'Online' : 'Offline'}
                                  size="small"
                                  sx={{
                                    mt: 1,
                                    background: screen.status ? '#e8f5e9' : '#fce4ec',
                                    color: screen.status ? '#2e7d32' : '#c62828',
                                    fontWeight: 600, fontSize: 11,
                                  }}
                                />
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Paper>
            )}
            {/* 탭 5: 메뉴 가격 */}
            {activeTab === 4 && (
              <MenuPriceEditor store={myStore} userId={userId} user={user} subuserId={opts.subuserId} />
            )}
            {/* 탭 6: 계약서 */}
            {activeTab === 5 && myStore && (
              <ContractTab storeId={myStore._id} storeName={myStore.name} />
            )}
          </>
        )}
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#551FFF' }}>변경 요청 제출</DialogTitle>
        <DialogContent>
          {myStores.length > 1 && (
            <FormControl fullWidth size="small" sx={{ mt: 1, mb: 1 }}>
              <InputLabel>대상 점포</InputLabel>
              <Select
                label="대상 점포"
                value={requestStoreId}
                onChange={e => setRequestStoreId(e.target.value)}
              >
                {myStores.map(s => (
                  <MenuItem key={s._id} value={s._id}>{s.tljNo || s.name || s._id}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>요청 유형</InputLabel>
                <Select name="type" value={requestForm.type} label="요청 유형" onChange={handleFormChange}>
                  {Object.entries(REQUEST_TYPES).map(([val, label]) => (
                    <MenuItem key={val} value={val}>{label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="메뉴 코드" name="menuCode" value={requestForm.menuCode} onChange={handleFormChange} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="메뉴 이름" name="menuName" value={requestForm.menuName} onChange={handleFormChange} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth size="small" label="변경 내용 *" name="changeDetails" value={requestForm.changeDetails}
                onChange={handleFormChange} multiline rows={4} placeholder="변경 사항을 자세히 설명해 주세요."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#888' }}>취소</Button>
          <Button
            variant="contained"
            onClick={handleSubmitRequest}
            disabled={submitting || !requestForm.changeDetails.trim() || (!requestStoreId && myStores.length > 1)}
            sx={{ background: '#551FFF', borderRadius: 2 }}
          >
            {submitting ? '제출 중...' : '제출'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

// ── 요청 카드 (상태 타임라인 포함) ──
function RequestCard({ req }) {
  const [expanded, setExpanded] = useState(false);
  const statusCfg = STATUS_COLORS[req.status] || STATUS_COLORS.pending;

  const SIZE_LABELS = { hot_s: 'HOT 12oz', hot_m: 'HOT 16oz', ice_s: 'ICED 16oz', ice_m: 'ICED 20oz' };

  // 진행률 계산
  const STATUS_ORDER = ['received', 'in_progress', 'customer_confirm', 'confirmed', 'updating', 'completed'];
  const currentIdx = STATUS_ORDER.indexOf(req.status);
  const progress = req.status === 'rejected' ? 0 : Math.max(0, ((currentIdx + 1) / STATUS_ORDER.length) * 100);

  return (
    <Box className="sp-request-item" sx={{ cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Chip label={REQUEST_TYPES[req.type] || req.type} size="small"
          sx={{ background: 'rgba(85,31,255,0.1)', color: '#551FFF', fontWeight: 600, fontSize: 11 }} />
        <Chip label={statusCfg.label} size="small"
          sx={{ background: statusCfg.bg, color: statusCfg.color, fontWeight: 600, fontSize: 11 }} />
        {req.store?.tljNo && <Chip label={req.store.tljNo} size="small" variant="outlined" sx={{ fontSize: 10 }} />}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {req.createdAt ? new Date(req.createdAt).toLocaleDateString('ko-KR') : ''}
        </Typography>
      </Box>

      {/* Progress bar */}
      {req.status !== 'completed' && req.status !== 'rejected' && (
        <Box sx={{ mt: 1, mb: 0.5 }}>
          <Box sx={{ height: 4, borderRadius: 2, background: '#eee', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${progress}%`, background: statusCfg.color, borderRadius: 2, transition: 'width 0.3s' }} />
          </Box>
        </Box>
      )}

      {req.menuCode && (
        <Typography variant="body2" sx={{ fontSize: 12, color: '#555', mt: 0.5 }}>
          {req.menuName || req.menuCode}
        </Typography>
      )}
      <Typography variant="body2" sx={{ fontSize: 13, mt: 0.5, whiteSpace: 'pre-line' }}>
        {req.changeDetails}
      </Typography>

      {/* Expanded: price changes + status timeline */}
      {expanded && (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed #e0e0e0' }}>
          {/* Price changes */}
          {req.priceChanges?.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#888', mb: 0.5 }}>
                Price Changes
              </Typography>
              {req.priceChanges.map((p, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3, fontSize: 12 }}>
                  <span style={{ color: '#555' }}>{SIZE_LABELS[p.sizeKey] || p.sizeKey}</span>
                  <span>
                    <span style={{ color: '#c62828', textDecoration: 'line-through' }}>${((p.oldPrice || 0) / 100).toFixed(2)}</span>
                    <span style={{ color: '#888', margin: '0 4px' }}>&rarr;</span>
                    <span style={{ color: '#2e7d32', fontWeight: 700 }}>${((p.newPrice || 0) / 100).toFixed(2)}</span>
                  </span>
                </Box>
              ))}
            </Box>
          )}

          {/* Admin note */}
          {req.adminNote && (
            <Box sx={{ p: 1, mb: 1.5, background: '#fef9c3', borderRadius: 1, fontSize: 12 }}>
              <strong>Admin:</strong> {req.adminNote}
            </Box>
          )}

          {/* Status timeline */}
          {req.statusLog?.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#888', mb: 0.5 }}>
                Progress
              </Typography>
              {req.statusLog.map((log, i) => {
                const logCfg = STATUS_COLORS[log.status] || STATUS_COLORS.pending;
                return (
                  <Box key={log._id || i} sx={{
                    display: 'flex', gap: 1, py: 0.5,
                    borderLeft: `2px solid ${i === req.statusLog.length - 1 ? logCfg.color : '#e5e7eb'}`,
                    pl: 1.5, ml: 0.5,
                  }}>
                    <Box sx={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: i === req.statusLog.length - 1 ? logCfg.color : '#ddd',
                      mt: 0.5, flexShrink: 0, ml: -1.8,
                    }} />
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip label={logCfg.label} size="small"
                          sx={{ height: 18, fontSize: 10, fontWeight: 600, background: logCfg.bg, color: logCfg.color }} />
                        {log.changedByName && (
                          <Typography sx={{ fontSize: 10, color: '#888' }}>{log.changedByName}</Typography>
                        )}
                      </Box>
                      {log.note && <Typography sx={{ fontSize: 11, color: '#666', mt: 0.2 }}>{log.note}</Typography>}
                      <Typography sx={{ fontSize: 10, color: '#aaa' }}>
                        {log.at ? new Date(log.at).toLocaleString('ko-KR') : ''}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Assigned designer */}
          {req.assignedToName && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: 11, color: '#888' }}>
              Assigned: <strong style={{ color: '#333' }}>{req.assignedToName}</strong>
            </Box>
          )}
        </Box>
      )}

      {/* Expand hint */}
      <Typography sx={{ fontSize: 10, color: '#bbb', mt: 0.5, textAlign: 'center' }}>
        {expanded ? '접기' : '상세 보기'}
      </Typography>
    </Box>
  );
}

// ── 계약서 탭 (매장주 포털) ──
function ContractTab({ storeId, storeName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/store/my-contract/${storeId}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [storeId]);

  if (loading) return <Box sx={{ py: 4, textAlign: 'center' }}><Typography sx={{ color: '#888' }}>Loading...</Typography></Box>;

  const c = data?.contract;
  const logs = data?.logs || [];
  const statusLabel = { none: '미발송', sent: '발송됨', signed: '서명 완료', expired: '만료' };
  const statusColor = { none: '#888', sent: '#1565c0', signed: '#22c55e', expired: '#e53935' };

  return (
    <Box>
      <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1.5 }}>계약 정보</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={3}>
            <Typography sx={{ fontSize: 11, color: '#888' }}>상태</Typography>
            <Chip label={statusLabel[c?.contractStatus] || '미발송'} size="small"
              sx={{ mt: 0.5, fontWeight: 700, color: statusColor[c?.contractStatus] || '#888',
                borderColor: statusColor[c?.contractStatus], fontSize: 12 }}
              variant="outlined" />
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography sx={{ fontSize: 11, color: '#888' }}>서명인</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{c?.contractSigneeName || '-'}</Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography sx={{ fontSize: 11, color: '#888' }}>서명일</Typography>
            <Typography sx={{ fontSize: 13 }}>
              {c?.contractSignedAt ? new Date(c.contractSignedAt).toLocaleDateString('ko-KR') : '-'}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Typography sx={{ fontSize: 11, color: '#888' }}>계약서</Typography>
            {c?.contractDocUrl ? (
              <Button size="small" variant="outlined" href={c.contractDocUrl} target="_blank"
                sx={{ mt: 0.3, fontSize: 11, borderColor: '#551FFF', color: '#551FFF' }}>
                PDF 보기
              </Button>
            ) : <Typography sx={{ fontSize: 13, color: '#ccc' }}>없음</Typography>}
          </Grid>
        </Grid>
      </Paper>

      {logs.length > 0 && (
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>이력</Typography>
          {logs.map((log, i) => (
            <Box key={log._id} sx={{ display: 'flex', gap: 1, py: 0.8, borderLeft: `2px solid ${i === 0 ? '#551FFF' : '#e5e7eb'}`, pl: 1.5, ml: 0.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: i === 0 ? '#551FFF' : '#ddd', mt: 0.5, flexShrink: 0, ml: -1.5 }} />
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: i === 0 ? 600 : 400 }}>
                  {log.action === 'uploaded' ? '파일 업로드' :
                   log.action === 'status_changed' ? '상태 변경' :
                   log.action === 'signed' ? '서명 완료' :
                   log.action === 'sent' ? '계약서 발송' : log.action}
                </Typography>
                {log.note && <Typography sx={{ fontSize: 11, color: '#888' }}>{log.note}</Typography>}
                <Typography sx={{ fontSize: 10, color: '#aaa' }}>
                  {new Date(log.createdAt).toLocaleString('ko-KR')}
                </Typography>
              </Box>
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
}

export default StorePortal;
